import './registration.less'

import {Icon} from '@codeurs/front'
import {EventModel} from 'models/EventModel'
import {
	RegistrationConfig,
	RegistrationModel
} from 'models/registration/RegistrationModel'
import {StepKey} from 'models/registration/StepModel'
import React, {useEffect, useState} from 'react'
import {Redirect} from 'react-router'
import {PageData} from 'types'
import useRouter from 'use-react-router'
import {CMSLink} from 'util/cmslink'
import {useEvent} from 'util/eventcontext'
import {scrollTo} from 'util/scrollmemory'
import {style} from 'util/style'
import {Cookiewarning} from 'views/layout/cookiewarning'
import {Shop, ShopContent} from 'views/registration/shop'
import {BadgeStep} from 'views/registration/steps/badgestep'
import {ConfigStep} from 'views/registration/steps/configstep'
import {DetailsStep} from 'views/registration/steps/detailsstep'
import {GiftStep} from 'views/registration/steps/giftstep'
import {OnsiteStep} from 'views/registration/steps/onsitestep'
import {SelectStep} from 'views/registration/steps/selectstep'
import {SummaryStep} from 'views/registration/steps/summarystep'
import {TouringStep} from 'views/registration/steps/touringstep'
import {Theme} from 'views/theme'
import {Tracking} from '../../util/tracking'

const styles = style('registration', ['hero'])

export const Registration: React.FC<PageData & RegistrationConfig> = data => {
	const event = useEvent()
	const step = (window.location.hash.substr(1) || 'select') as StepKey

	const {
		location: {pathname}
	} = useRouter()
	const [registration, updateRegistration] = useState<RegistrationModel>(() => {
		const stateStr = window.sessionStorage[`registration_${data.id}`]
		return new RegistrationModel(
			{...data, eventID: event.id},
			event,
			stateStr && JSON.parse(stateStr)
		)
	})
	const updateAndSaveRegistration = (reg: RegistrationModel) => {
		updateRegistration(reg)
		const {confirmation, ...state} = reg.state
		window.sessionStorage[`registration_${registration.id}`] = JSON.stringify(
			state
		)
	}

	useEffect(() => {
		scrollTo(() => 0, document.querySelector('.shop'))
	}, [step])

	const actualKey = registration.confirmStepKey(step)
	if (step !== actualKey) return <Redirect to={pathname + `#${actualKey}`} />

	return (
		<div className={styles}>
			<CMSLink id={data.id} project={data.project_key} />
			<Tracking view={Cookiewarning} project={data.project_key} />
			<RegistrationHero
				event={event}
				registration={registration}
				updateRegistration={updateRegistration}
			/>
			<Shop>
				<ShopContent>
					<RegistrationStep
						step={step}
						registration={registration}
						updateRegistration={updateAndSaveRegistration}
					/>
				</ShopContent>
				{/* <ShopBasket registration={registration} /> */}
			</Shop>
		</div>
	)
}

export const RegistrationStep: React.FC<{
	step: StepKey
	registration: RegistrationModel
	updateRegistration: (model: RegistrationModel) => void
}> = props => {
	switch (props.step) {
		case 'select':
			return <SelectStep {...props} />
		case 'breakouts':
			return <ConfigStep {...props} />
		case 'configure':
			return <ConfigStep {...props} />
		case 'details':
			return <DetailsStep {...props} />
		case 'gift':
			return <GiftStep {...props} />
		case 'touring':
			return <TouringStep {...props} />
		case 'summary':
			return <SummaryStep {...props} />
		case 'onsite':
			return <OnsiteStep {...props} />
		case 'badge':
			return <BadgeStep {...props} />
		default:
			return <p>Step not found</p>
	}
}

export const RegistrationHero: React.FC<{
	event: EventModel
	registration: RegistrationModel
	updateRegistration: (registration: RegistrationModel) => void
}> = attrs => {
	const {event, registration, updateRegistration} = attrs

	return (
		<div
			{...styles.hero}
			style={
				event?.background?.src && {
					backgroundImage: `url(${event.background.src})`
				}
			}
		>
			<div {...styles.hero('outer')}>
				<Theme.Container {...styles.hero('inner')}>
					{!registration.isOnsite && (
						<div {...styles.hero('backlinks')}>
							{registration.backLinks?.length > 0 &&
								registration.backLinks.map(
									(link, i) =>
										link?.url && (
											<Theme.Link
												href={link.url}
												mod="inherit"
												{...styles.hero('backlinks-item')}
												key={i}
											>
												<Icon
													icon="arrow-left"
													{...styles.hero('backlinks-item-icon')}
												/>
												{link.description || link.title}
											</Theme.Link>
										)
								)}
							{(!registration.backLinks ||
								registration.backLinks?.length === 0) && (
								<Theme.Link
									href={event.url}
									mod="inherit"
									{...styles.hero('backlinks-item')}
								>
									<Icon
										icon="arrow-left"
										{...styles.hero('backlinks-item-icon')}
									/>
									{`Back to ${event.title}`}
								</Theme.Link>
							)}
						</div>
					)}
					{registration.isOnsite && (
						<Theme.Button
							{...styles.hero('back')}
							mod={['light', 'outline']}
							onClick={() => updateRegistration(registration.clear())}
						>
							<Theme.ButtonIcon icon={'close'} />
							<Theme.ButtonText>Clear Registration</Theme.ButtonText>
						</Theme.Button>
					)}
					{!registration.isOnsite && registration.contactLink?.url && (
						<a
							target="_blank"
							href={registration.contactLink.url}
							class={styles.hero('contact')}
						>
							<span {...styles.hero('contact-before')}>?</span>
							{registration.contactLink?.description ||
								registration.contactLink.title}
						</a>
					)}
				</Theme.Container>
			</div>
		</div>
	)
}
